import { useState, useEffect, useRef } from "react";
import { FLEX_COL_FILL } from "../../../../../themes/sbux/components";
import { useAppDispatch, useAppSelector } from "../../../../../store/types";
import { Box } from "@mui/material";
import SbuxEnhancedTable from "../../../../../components/SbuxTable/SbuxEnhancedTable";
import SbuxStackSnackbar from "../../../../../components/SbuxStackSnackbar";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import ResourceDialog from "./ResourceDialog";
import {
  getResources,
  createResource,
  updateResource,
  deleteResource,
} from "../../../../../services/resources";
import {
  getResourcesDataSelector,
  getResourceStatusSelector,
  getResourceErrorMessageSelector,
} from "../../../../../selectors/resourcesSelector";
import { getInstanceIdSelector } from "../../../../../selectors/userInstanceSelector";
import { getCcpAgentRoutingNameSelector } from "../../../../../selectors/ccpSelector";
import {
  SNACK_BARS_SEVERITY_ERROR,
  SNACK_BARS_SEVERITY_SUCCESS,
} from "../../../../../constants";
import { columns } from "./columns";
import ResourceRow from "./ResourceRow";
import { normalizeResourceData, getExistingGroups } from "./utils";
import { logMessage } from "../../../../../utils/amplifyLogger";
import SbuxLoader from "../../../../../components/SbuxLoader";
import type { ReactNode } from "react";

interface ResourceRowData {
  name?: string;
  lobName?: string;
  itemIndex?: number;
  resourceName?: string;
  resourceUrl?: string;
  [key: string]: unknown;
}

const ResourceManagerTable = () => {
  const dispatch = useAppDispatch();
  const resourcesData = useAppSelector(getResourcesDataSelector);
  const resourceStatus = useAppSelector(getResourceStatusSelector);
  const resourceErrorMessage = useAppSelector(getResourceErrorMessageSelector);
  const instanceId = useAppSelector(getInstanceIdSelector);
  const agentRoutingName = useAppSelector(getCcpAgentRoutingNameSelector);

  const [resources, setResources] = useState<Record<string, unknown>[]>([]);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState<ReactNode>("");
  const [snackBarSeverity, setSnackBarSeverity] = useState(
    SNACK_BARS_SEVERITY_SUCCESS,
  );
  const isCrudOperation = useRef(false);

  const [openAddNew, setOpenAddNew] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<ResourceRowData | null>(null);

  useEffect(() => {
    if (instanceId && agentRoutingName) {
      dispatch(getResources());
    }
  }, [dispatch, instanceId, agentRoutingName]);

  useEffect(() => {
    if (resourceStatus === "success") {
      const transformedData = normalizeResourceData(
        resourcesData as Parameters<typeof normalizeResourceData>[0],
      );
      setResources(transformedData);
    }
  }, [resourcesData, resourceStatus]);

  const handleAddNew = () => setOpenAddNew((prev) => !prev);

  const handleEditRowClick = (row: Record<string, unknown>) => {
    setEditRow(row as ResourceRowData);
    setIsEditOpen(true);
  };
  const handleCloseEdit = () => setIsEditOpen(false);

  const handleSaveNew = async (payload: Record<string, unknown>) => {
    isCrudOperation.current = true;
    try {
      const resourceItem = {
        name: payload.name as string,
        lobName: payload.lobName as string,
        resourceName: payload.resourceName as string,
        resourceUrl: payload.resourceUrl as string,
      };

      await dispatch(createResource(resourceItem));
      await dispatch(getResources());
      setOpenAddNew(false);
    } catch (error) {
      isCrudOperation.current = false;
      logMessage(
        "ResourceManagerTable",
        `Error creating resource: ${String(error)}`,
        "error",
      );
    }
  };

  const handleSaveEdit = async (payload: Record<string, unknown>) => {
    isCrudOperation.current = true;
    try {
      if (!editRow) return;
      const resourceItem = {
        name: editRow.name,
        lobName: editRow.lobName,
        itemIndex: editRow.itemIndex,
        resourceName: payload.resourceName as string,
        resourceUrl: payload.resourceUrl as string,
      };

      await dispatch(updateResource(resourceItem));
      await dispatch(getResources());
      setIsEditOpen(false);
    } catch (error) {
      isCrudOperation.current = false;
      logMessage(
        "ResourceManagerTable",
        `Error updating resource: ${String(error)}`,
        "error",
      );
    }
  };

  const handleDelete = async (row: Record<string, unknown>) => {
    isCrudOperation.current = true;
    try {
      const resourceItem = {
        name: row.name as string,
        lobName: row.lobName as string,
        itemIndex: row.itemIndex as number,
        resourceName: row.resourceName as string,
        resourceUrl: row.resourceUrl as string,
      };

      await dispatch(deleteResource(resourceItem));
      await dispatch(getResources());
    } catch (error) {
      isCrudOperation.current = false;
      logMessage(
        "ResourceManagerTable",
        `Error deleting resource: ${String(error)}`,
        "error",
      );
    }
  };

  const handleRefresh = () => {
    dispatch(getResources());
  };

  useEffect(() => {
    if (resourceStatus === "success") {
      isCrudOperation.current = false;
    }
  }, [resourceStatus]);

  useEffect(() => {
    if (resourceStatus === "create:success") {
      setOpenSnackBar(false);
      setTimeout(() => {
        setSnackBarMessage(
          <SbuxTranslate>{"Resource created successfully"}</SbuxTranslate>,
        );
        setSnackBarSeverity(SNACK_BARS_SEVERITY_SUCCESS);
        setOpenSnackBar(true);
      }, 0);
    } else if (resourceStatus === "update:success") {
      setOpenSnackBar(false);
      setTimeout(() => {
        setSnackBarMessage(
          <SbuxTranslate>{"Resource updated successfully"}</SbuxTranslate>,
        );
        setSnackBarSeverity(SNACK_BARS_SEVERITY_SUCCESS);
        setOpenSnackBar(true);
      }, 0);
    } else if (resourceStatus === "delete:success") {
      setOpenSnackBar(false);
      setTimeout(() => {
        setSnackBarMessage(
          <SbuxTranslate>{"Resource deleted successfully"}</SbuxTranslate>,
        );
        setSnackBarSeverity(SNACK_BARS_SEVERITY_SUCCESS);
        setOpenSnackBar(true);
      }, 0);
    } else if (
      resourceStatus === "create:rejected" ||
      resourceStatus === "update:rejected" ||
      resourceStatus === "delete:rejected" ||
      resourceStatus === "rejected"
    ) {
      setOpenSnackBar(false);
      setTimeout(() => {
        setSnackBarMessage(
          resourceErrorMessage || (
            <SbuxTranslate>{"An error occurred"}</SbuxTranslate>
          ),
        );
        setSnackBarSeverity(SNACK_BARS_SEVERITY_ERROR);
        setOpenSnackBar(true);
      }, 0);
    }
  }, [resourceStatus, resourceErrorMessage]);

  const handleCloseSnackbar = () => {
    setOpenSnackBar(false);
  };

  return (
    <>
      <Box sx={FLEX_COL_FILL}>
        {resourceStatus === "pending" && !isCrudOperation.current ? (
          <SbuxLoader />
        ) : (
          <SbuxEnhancedTable
            rows={resources}
            columns={
              columns as unknown as {
                id: string;
                label: string;
                [key: string]: unknown;
              }[]
            }
            defaultSortingField={"resourceName"}
            defaultSortOrder={"asc"}
            RowSource={
              ResourceRow as unknown as Parameters<
                typeof SbuxEnhancedTable extends (...args: infer P) => unknown
                  ? (...args: P) => unknown
                  : never
              >[0]["RowSource"]
            }
            showFooter={true}
            showSearchBar={true}
            isManager={true}
            showAddNewButton={true}
            addNewButtonTitle={<SbuxTranslate>{"Add New"}</SbuxTranslate>}
            handleAddNewButtonClick={handleAddNew}
            handleEditRowClick={handleEditRowClick}
            handleDeleteRowClick={handleDelete}
            showRefreshButton={true}
            handleRefreshClick={handleRefresh}
            footerMarginBottom={"0"}
          />
        )}
      </Box>

      {openAddNew && (
        <ResourceDialog
          open={openAddNew}
          toggleDialogOpen={handleAddNew}
          row={{ instanceId: instanceId ?? undefined }}
          isAddNew={true}
          handleSave={handleSaveNew}
          groupOptions={getExistingGroups(
            resourcesData as Parameters<typeof getExistingGroups>[0],
          )}
          existingResources={
            resourcesData as Parameters<typeof getExistingGroups>[0]
          }
        />
      )}

      {isEditOpen && editRow && (
        <ResourceDialog
          open={isEditOpen}
          toggleDialogOpen={handleCloseEdit}
          row={editRow}
          isAddNew={false}
          handleSave={handleSaveEdit}
          groupOptions={getExistingGroups(
            resourcesData as Parameters<typeof getExistingGroups>[0],
          )}
          existingResources={
            resourcesData as Parameters<typeof getExistingGroups>[0]
          }
        />
      )}

      {openSnackBar && (
        <SbuxStackSnackbar
          width={380}
          open={openSnackBar}
          message={snackBarMessage}
          severity={snackBarSeverity}
          autoHide={snackBarSeverity !== SNACK_BARS_SEVERITY_ERROR}
          autoHideDuration={
            snackBarSeverity !== SNACK_BARS_SEVERITY_ERROR ? 30000 : undefined
          }
          handleClose={handleCloseSnackbar}
        />
      )}
    </>
  );
};

export default ResourceManagerTable;
